<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Parcela' : 'Nova Compra' }}
      </h1>
      <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[92px]">
        <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg mr-1">progress_activity</span>
        <span>{{ isSubmitting ? 'Salvando...' : 'Salvar' }}</span>
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div>
            <label class="form-label">Banco</label>
            <select v-model="gastoCartao.banco" required class="form-select">
              <option disabled value="">Selecione um banco</option>
              <option v-for="name in bancos" :key="name" :value="name">{{ name }}</option>
            </select>
          </div>
          <div class="relative" ref="categoryDropdownContainer">
            <label class="form-label">Categoria</label>
            <button type="button" class="form-input flex items-center justify-between text-left"
                    @click="isCategoryDropdownOpen = !isCategoryDropdownOpen">
              <span v-if="gastoCartao.categoria" class="flex items-center gap-2">
                <span class="material-symbols-outlined text-lg">{{ getCategoryIcon(gastoCartao.categoria) }}</span>
                <span>{{ gastoCartao.categoria }}</span>
              </span>
              <span v-else class="text-slate-400 dark:text-slate-500">Selecione</span>
              <span class="material-symbols-outlined text-slate-400">unfold_more</span>
            </button>
            <transition name="fade-fast">
              <div v-if="isCategoryDropdownOpen"
                   class="absolute z-20 w-full mt-1 bg-white dark:bg-slate-900 rounded-lg shadow-lg border dark:border-slate-700 max-h-56 overflow-y-auto">
                <ul>
                  <li v-for="(icon, name) in categoryIcons" :key="name" @click="selectCategory(name)"
                      class="px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-3">
                    <span class="material-symbols-outlined text-lg">{{ icon }}</span>
                    <span>{{ name }}</span>
                  </li>
                </ul>
              </div>
            </transition>
          </div>
        </div>

        <div>
          <label class="form-label">Descrição</label>
          <input v-model="gastoCartao.descricao" type="text" required placeholder="Ex: Compra na Amazon" class="form-input" />
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div>
            <label class="form-label">{{ isEditing ? 'Valor da Parcela (R$)' : 'Valor Total (R$)' }}</label>
            <input v-if="isEditing" v-model.number="gastoCartao.valorParcela" type="number" step="0.01" required placeholder="0.00" class="form-input" />
            <input v-else v-model.number="gastoCartao.valorTotal" type="number" step="0.01" required placeholder="0.00" class="form-input" />
          </div>
          <div>
            <label class="form-label">{{ isEditing ? 'Data de Vencimento' : 'Data da 1ª Parcela' }}</label>
            <input v-model="gastoCartao.dataPagamento" type="date" required class="form-input" />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div v-if="!isEditing">
            <label class="form-label">Nº de Parcelas</label>
            <input v-model.number="gastoCartao.numeroParcelas" type="number" min="1" placeholder="1" class="form-input" />
          </div>
          <div>
            <label class="form-label">Status</label>
            <select v-model="gastoCartao.status" required class="form-select">
              <option value="PENDENTE">Pendente</option>
              <option value="PAGO">Pago</option>
              <option value="CONGELADO">Congelado</option>
            </select>
          </div>
        </div>

        <div v-if="isEditing" class="pt-1">
          <p class="form-label mb-2">Aplicar alterações em:</p>
          <div class="flex items-center space-x-4">
            <label class="flex items-center cursor-pointer">
              <input type="radio" v-model="gastoCartao.tipoEdicao" value="UNICO" class="form-radio">
              <span class="ml-2 text-sm">Apenas nesta parcela</span>
            </label>
            <label class="flex items-center cursor-pointer">
              <input type="radio" v-model="gastoCartao.tipoEdicao" value="TODOS" class="form-radio">
              <span class="ml-2 text-sm">Nesta e nas próximas</span>
            </label>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'

const route = useRoute()
const router = useRouter()

const apiBaseUrl = 'https://backend.controlease.com.br'
const apiUrl = `${apiBaseUrl}/gastos-cartao`

const bancos = ['Santander','Nubank','Banco do Brasil','C6 Bank','Itaú','Bradesco','Caixa','Inter','PagBank','PicPay','Mercado Pago','Renner','Amazon','Shopee','Outro']
const categoryIcons = { 'Alimentação':'restaurant','Transporte':'commute','Moradia':'home','Lazer':'sports_esports','Saúde':'health_and_safety','Educação':'school','Compras':'shopping_bag','Serviços':'dns','Viagem':'flight','Padrão':'receipt_long' }
const getCategoryIcon = (name) => categoryIcons[name] || categoryIcons['Padrão']

const isEditing = computed(() => !!route.params.id)
const isSubmitting = ref(false)
const isCategoryDropdownOpen = ref(false)
const categoryDropdownContainer = ref(null)

const authConfig = () => {
  const token = localStorage.getItem('token')
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}
const showToast = (m) => console.log('[toast]', m)

const toISODateInput = (dateString) => {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (isNaN(d.getTime())) return ''
  const offset = d.getTimezoneOffset()
  const adjusted = new Date(d.getTime() - offset*60*1000)
  return adjusted.toISOString().split('T')[0]
}

const getInitial = () => ({
  id: null,
  descricao: '',
  categoria: 'Padrão',
  banco: '',
  status: 'PENDENTE',
  valorTotal: null,
  valorParcela: null,
  numeroParcelas: 1,
  dataPagamento: new Date().toISOString().split('T')[0],
  tipoEdicao: 'UNICO',
  dataQuitacaoReal: null
})
const gastoCartao = ref(getInitial())

const hydrateFromData = (data) => {
  gastoCartao.value = {
    ...getInitial(),
    ...data,
    id: data.id,
    valorParcela: Number(data.valorParcela ?? 0),
    dataPagamento: toISODateInput(data.dataPagamento),
    tipoEdicao: data.tipoEdicao ?? 'UNICO'
  }
}

const fetchGastoById = async (id) => {
  try {
    const { data } = await axios.get(`${apiUrl}/${id}`, authConfig())
    hydrateFromData(data)
  } catch (e) {
    console.error('Erro ao buscar parcela por ID:', e?.response || e)
    const status = e?.response?.status
    if (status === 404) {
      showToast('Parcela não encontrada.')
      goBack()
    } else {
      showToast('Erro ao carregar. Tente novamente.')
    }
  }
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  try {
    if (isEditing.value) {
      const payload = {
        id: gastoCartao.value.id || route.params.id,
        banco: gastoCartao.value.banco,
        categoria: gastoCartao.value.categoria,
        descricao: gastoCartao.value.descricao,
        valorParcela: Number(gastoCartao.value.valorParcela ?? 0),
        dataPagamento: gastoCartao.value.dataPagamento || null,
        status: gastoCartao.value.status,
        tipoEdicao: gastoCartao.value.tipoEdicao || 'UNICO'
      }
      await axios.put(`${apiUrl}/${payload.id}`, payload, authConfig())
    } else {
      const payload = {
        banco: gastoCartao.value.banco,
        categoria: gastoCartao.value.categoria,
        descricao: gastoCartao.value.descricao,
        valorTotal: Number(gastoCartao.value.valorTotal ?? 0),
        numeroParcelas: Math.max(1, parseInt(gastoCartao.value.numeroParcelas, 10) || 1),
        dataPagamento: gastoCartao.value.dataPagamento || null,
        status: gastoCartao.value.status
      }
      await axios.post(apiUrl, payload, authConfig())
    }
    goBack()
  } catch (e) {
    console.error('Erro ao salvar:', e)
    showToast(e?.response?.data?.message || 'Erro ao salvar.')
  } finally {
    isSubmitting.value = false
  }
}

const selectCategory = (name) => {
  gastoCartao.value.categoria = name
  isCategoryDropdownOpen.value = false
}
const handleClickOutside = (event) => {
  if (categoryDropdownContainer.value && !categoryDropdownContainer.value.contains(event.target)) {
    isCategoryDropdownOpen.value = false
  }
}
const goBack = () => router.push('/user/gastos-cartao')

const waitForToken = async (timeoutMs = 1200) => {
  const t0 = Date.now()
  while (!localStorage.getItem('token') && Date.now() - t0 < timeoutMs) {
    await new Promise(r => setTimeout(r, 50))
  }
}

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)

  if (isEditing.value) {
    await waitForToken()           
    const id = route.params.id
    const navStateItem = window.history?.state?.item
    if (navStateItem && String(navStateItem.id) === String(id)) {
      hydrateFromData(navStateItem)
    }
    await fetchGastoById(id)
  } else {
    gastoCartao.value = getInitial()
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.panel {
  @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20;
}
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-[#5A189A] hover:bg-[#3C096C] text-white shadow-lg shadow-[#5A189A]/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.form-input,.form-select {
  @apply w-full px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-100 bg-slate-100/80 dark:bg-slate-800/70 rounded-xl transition ease-in-out ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-[#5A189A] focus:bg-white dark:focus:bg-slate-900;
}
.form-select { @apply appearance-none; }
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5; }
.form-radio { @apply focus:ring-purple-500 h-4 w-4 text-purple-600 border-slate-300 dark:border-slate-500 dark:bg-slate-600 dark:checked:bg-purple-500; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.fade-fast-enter-active, .fade-fast-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.fade-fast-enter-from, .fade-fast-leave-to { opacity: 0; transform: scale(0.98) translateY(-4px); }
</style>
